// Same visual language as the other system pages (Departments, Broadcasts):
// glass header card tucked under the topbar, glass section cards with an icon
// title, unifiedControlStyle inputs and the #2e7cba accent.
import { unifiedControlStyle } from "../../../components/ReusableDropdown";

export const BRAND = "#2e7cba";
export const BRAND_DARK = "#256a9f";

export const getAdminAccountStyles = (screenSize, theme, language) => {
    const { isMobile } = screenSize;
    const isRTL = language === "ar";
    const dark = theme === "dark";

    const c = {
        text: dark ? "#ffffff" : "#0f172a",
        body: dark ? "rgba(255, 255, 255, 0.85)" : "#334155",
        muted: dark ? "rgba(255, 255, 255, 0.6)" : "#64748b",
        border: dark ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0",
        chipBg: dark ? "rgba(46, 124, 186, 0.2)" : "rgba(46, 124, 186, 0.08)",
        chipText: dark ? "#8cc4ee" : BRAND,
    };

    const glass = {
        background: dark ? "rgba(255, 255, 255, 0.02)" : "rgba(255, 255, 255, 0.9)",
        border: dark ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid rgba(0, 0, 0, 0.05)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        boxShadow: dark
            ? "0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
            : "0 8px 32px rgba(0, 0, 0, 0.08)",
    };

    const btnBase = {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        padding: isMobile ? "11px 16px" : "12px 22px",
        borderRadius: "12px",
        fontSize: "14px",
        fontWeight: 700,
        fontFamily: "inherit",
        whiteSpace: "nowrap",
        border: "1px solid transparent",
        transition: "all 0.2s ease",
        outline: "none",
    };

    return {
        colors: c,
        container: {
            padding: isMobile ? "0 12px" : "0",
            direction: isRTL ? "rtl" : "ltr",
            color: c.text,
        },
        header: {
            ...glass,
            borderRadius: isMobile ? "16px" : "24px",
            padding: isMobile ? "20px" : "32px",
            marginTop: "-36px",
            marginBottom: "24px",
            animation: "aacSlideDown 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
        },
        title: {
            fontSize: isMobile ? "22px" : "28px",
            fontWeight: 700,
            letterSpacing: isRTL ? 0 : "-0.5px",
            color: dark ? "#f1f5f9" : "#1e293b",
            margin: 0,
            display: "flex",
            alignItems: "center",
            gap: "12px",
        },
        subtitle: {
            fontSize: isMobile ? "14px" : "15px",
            fontWeight: 500,
            color: dark ? "#cbd5e1" : "#64748b",
            margin: "12px 0 0 0",
            lineHeight: 1.6,
        },
        sectionCard: {
            ...glass,
            borderRadius: "16px",
            padding: isMobile ? "18px" : "24px",
            marginBottom: "24px",
        },
        sectionHeader: {
            marginBottom: isMobile ? "16px" : "22px",
            paddingBottom: isMobile ? "12px" : "16px",
            borderBottom: `1px solid ${dark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
        },
        sectionTitle: {
            fontSize: isMobile ? "16px" : "18px",
            fontWeight: 700,
            color: c.text,
            margin: 0,
            display: "flex",
            alignItems: "center",
            gap: "10px",
        },
        sectionSubtitle: { fontSize: "13.5px", color: c.muted, margin: "4px 0 0 0" },
        grid: {
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
            gap: isMobile ? "16px" : "20px",
        },
        field: { display: "flex", flexDirection: "column", gap: "8px", minWidth: 0 },
        label: {
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "13px",
            fontWeight: 700,
            color: dark ? "rgba(255, 255, 255, 0.9)" : "#374151",
        },
        required: { color: "#ef4444" },
        optional: { fontWeight: 500, color: c.muted },
        input: unifiedControlStyle(theme, isRTL),
        passwordWrap: { position: "relative" },
        eyeBtn: {
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            insetInlineEnd: "8px",
            width: "32px",
            height: "32px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            borderRadius: "8px",
            background: "transparent",
            color: c.muted,
            cursor: "pointer",
        },
        notice: {
            display: "flex",
            gap: "10px",
            alignItems: "flex-start",
            padding: "12px 14px",
            borderRadius: "12px",
            fontSize: "13.5px",
            lineHeight: 1.6,
            background: c.chipBg,
            color: c.chipText,
            border: "1px solid rgba(46, 124, 186, 0.25)",
        },
        notices: { display: "flex", flexDirection: "column", gap: "10px", marginBottom: "24px" },
        actions: {
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            flexDirection: isMobile ? "column-reverse" : "row",
        },
        btnPrimary: (disabled = false) => ({
            ...btnBase,
            background: BRAND,
            color: "#ffffff",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.55 : 1,
        }),
        btnSecondary: (disabled = false) => ({
            ...btnBase,
            background: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.05)",
            color: c.body,
            borderColor: c.border,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.55 : 1,
        }),
    };
};

export const getAdminAccountGlobalCss = (theme) => `
  .aac-input:focus { border-color: rgba(46,124,186,0.6) !important; box-shadow: 0 0 0 3px rgba(46,124,186,0.12); }
  .aac-input::placeholder { color: ${theme === "dark" ? "rgba(203,213,225,0.5)" : "rgba(100,116,139,0.6)"}; }
  .aac-input:-webkit-autofill,
  .aac-input:-webkit-autofill:hover,
  .aac-input:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 30px ${theme === "dark" ? "#0f172a" : "#ffffff"} inset !important;
    -webkit-text-fill-color: ${theme === "dark" ? "#f1f5f9" : "#0f172a"} !important;
  }
  .aac-btn-primary:hover:not(:disabled) { background: ${BRAND_DARK} !important; }
  .aac-eye:hover { background: ${theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)"} !important; }
  .aac-scope *:focus-visible { outline: 2px solid ${BRAND}; outline-offset: 2px; }
  @keyframes aacSlideDown { from { opacity: 0; transform: translateY(-16px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes aacSpin { to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) { .aac-scope * { animation: none !important; transition: none !important; } }
`;

export default getAdminAccountStyles;
